import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Input,
  Select,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import DateTime from "react-datetime";
import { FilterContainer } from "../../../components/Tables/FilterContainer";
import { LOCALE_PT_BR } from "constants/date-time";
import { ViolationSituation } from "services/trafficViolationService";
import { aitFilterMask, plateMask, validateVehiclePlate } from "utils/forms-utils";
import { parseDateBrToISO } from "utils/utils";

export const TrafficViolationFilter = (props) => {
  const { t } = useTranslation('common');
  const { onFilter, filterSnapshot } = props;
  const { isOpen, onToggle } = useDisclosure();
  const [plate, setPlate] = useState("");
  const [aitNumber, setAitNumber] = useState("");
  const [situation, setSituation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const situations = useMemo(() => [
    ViolationSituation.NOT_VALIDATED,
    ViolationSituation.VALIDATED,
    ViolationSituation.CANCELED,
    ViolationSituation.DEFERRED,
    ViolationSituation.NP_GENERATED,
    ViolationSituation.NA_GENERATED,
  ], []);

  const isStartDateInvalid = startDate ? startDate > new Date() : false;
  const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
  const [plateError, setPlateError] = useState(null);

  const handleAitNumberChange = (e) => {
    setAitNumber(aitFilterMask(e.target.value));
  };

  const handlePlateChange = (e) => {
    const maskedPlate = plateMask(e.target.value);
    setPlate(maskedPlate);
    if (plateError) setPlateError(null);
  };

  const onFilterHandler = () => {
    onFilter({
      placa: plate,
      numero_ait: aitNumber,
      situacao: situation,
      data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
      data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : "",
    });
  }

  const onClearHandler = () => {
    setPlate("");
    setAitNumber("");
    setSituation("");
    setStartDate("");
    setEndDate("");
    setPlateError(null);

    onFilter({
      placa: null,
      numero_ait: null,
      situacao: null,
      data_inicial: null,
      data_final: null,
    });
  }

  const handleToggle = () => {
    if (isOpen) setPlateError(null);
    onToggle();
  };

  useLayoutEffect(() => {
    if (filterSnapshot == null) {
      setPlate("");
      setAitNumber("");
      setSituation("");
      setStartDate("");
      setEndDate("");
      return;
    }
    setPlate(filterSnapshot.placa ? String(filterSnapshot.placa) : "");
    setAitNumber(filterSnapshot.numero_ait ? String(filterSnapshot.numero_ait) : "");
    setSituation(filterSnapshot.situacao ? String(filterSnapshot.situacao) : "");
    if (filterSnapshot.data_inicial) {
      const d = parseDateBrToISO(filterSnapshot.data_inicial);
      setStartDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setStartDate("");
    }
    if (filterSnapshot.data_final) {
      const d = parseDateBrToISO(filterSnapshot.data_final);
      setEndDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setEndDate("");
    }
  }, [filterSnapshot]);

  return (
    <FilterContainer
      w='125px'
      fontSize='sm'
      fontWeight='bold'
      variant="light"
      isOpen={isOpen}
      onToggle={handleToggle}
      onFilter={onFilterHandler}
      onClear={onClearHandler}
    >
      <FormControl isInvalid={!!plateError}>
        <FormLabel>
          {t("traffic-violation.labels.plate")}
        </FormLabel>
        <Input
          value={plate}
          fontSize="sm"
          placeholder={t("traffic-violation.placeholders.plate")}
          _placeholder={{ color: placeholderColor, fontSize: "sm" }}
          onChange={handlePlateChange}
          onBlur={() => {
            const err = validateVehiclePlate(plate, t("traffic-violation.labels.plate"));
            setPlateError(err);
          }}
        />
        <FormErrorMessage>
          {plateError && t(plateError)}
        </FormErrorMessage>
      </FormControl>

      <FormControl>
        <FormLabel>
          {t("traffic-violation.labels.ait-number")}
        </FormLabel>
        <Input
          value={aitNumber}
          fontSize="sm"
          placeholder={t("traffic-violation.placeholders.ait-number")}
          _placeholder={{ color: placeholderColor, fontSize: "sm" }}
          onChange={handleAitNumberChange}
          maxLength={10}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          {t("traffic-violation.labels.situation-title")}
        </FormLabel>
        <Select
          fontSize="sm"
          id="violation-situation"
          value={situation}
          onChange={e => setSituation(e.currentTarget.value)}
          placeholder={t("traffic-violation.placeholders.situation")}
        >
          {situations?.map((option, index) => (
            <option key={index} value={option.value}>
              {t(`traffic-violation.labels.situation.${option.labelKey}`)}
            </option>
          ))}
        </Select>
      </FormControl>

      <Flex w="100%" gap="4" justify="space-between">
        <FormControl w="230px" isInvalid={isStartDateInvalid}>
          <FormLabel>
            {t("system.labels.start-date")}
          </FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={date => setStartDate(date)} />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-start-date")}
          </FormErrorMessage>
        </FormControl>

        <FormControl w="230px" isInvalid={isEndDateInvalid}>
          <FormLabel>
            {t("system.labels.end-date")}
          </FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={date => setEndDate(date)} />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-end-date")}
          </FormErrorMessage>
        </FormControl>
      </Flex>

      <Button
        disabled
        w="100%"
        variant="unstyled"
        borderTop="gray"
        borderTopColor="gray"
        onClick={onToggle}
      >
        <Flex gap="2" direction="column">
          <Divider orientation="horizontal" />

          <Flex px="4" align="center" justify="space-between">
            <Text fontWeight="bold">
              {t("traffic-violation.actions.pay-for-plate")}
            </Text>

            <ChevronRightIcon h="5" w="5"/>
          </Flex>

          <Divider orientation="horizontal" />
        </Flex>
      </Button>
    </FilterContainer>
  );
}